import { Input as AntInput, Modal, Button } from 'antd';
import { type PropsWithChildren, type FC, useEffect, useRef, type SyntheticEvent } from 'react';
import { type TOppositionBase } from '../../../../types/oppositionBase';
import { useState } from 'react';
import { Input } from '../../../../components';
import { useCreateOppositionBase, useRemoveOppositionBase, useUpdateOppositionBase } from '../../../../services/oppositionBase';
import clsx from 'clsx';
import { IconFile, IconUpload } from '@tabler/icons-react';
const { TextArea } = AntInput;

const fileToBase64 = async (file: File): Promise<string> => {
  return await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      if (event.target?.result) {
        resolve(event.target.result as string);
      } else {
        reject(new Error('Error al leer el archivo.'));
      }
    };
    reader.onerror = () => reject(new Error('Error al leer el archivo.'));
    reader.readAsDataURL(file);
  });
};

type Props = PropsWithChildren<{
  isOpen: boolean
  oppositionBase?: TOppositionBase | undefined
  onCancel: () => void
  title: string
}>;

const ManageOppositionBases: FC<Props> = ({ isOpen, onCancel, oppositionBase, title }) => {
  const { updateOppositionBase, loading: updateLoading } = useUpdateOppositionBase();
  const { createOppositionBase, loading: createLoading } = useCreateOppositionBase();
  const [{ name, description }, setEditedBase] = useState<Pick<TOppositionBase, 'name' | 'description'>>({ name: '', description: '' });
  const [fileBase64, setFileBase64] = useState<string>('');
  const [filename, setFilename] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onChangeBase = (value: string, field: string) => {
    setEditedBase((prev) => ({ ...prev, [field]: value }));
  };

  const onFileSelect = async (event: SyntheticEvent) => {
    const files = (event.target as HTMLInputElement).files;
    if (!files?.length) return;

    const file = files[0];
    if (file.type !== 'application/pdf') return;

    const base64 = await fileToBase64(file);
    setFileBase64(base64);
    setFilename(file.name);
  };

  const onClickUpload = (event: SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();
    fileInputRef.current?.click();
  };

  const resetForm = () => {
    setEditedBase({ name: '', description: '' });
    setFileBase64('');
    setFilename('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const onSaveBase = async () => {
    if (oppositionBase?._id) {
      await updateOppositionBase({
        _id: oppositionBase._id,
        name,
        description,
        ...(fileBase64 && filename ? { file: fileBase64, filename } : {})
      });
    } else {
      await createOppositionBase({ name, description, file: fileBase64, filename });
    }

    resetForm();
    onCancel();
  };

  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return;
    }

    if (oppositionBase?._id) {
      setEditedBase({ name: oppositionBase.name, description: oppositionBase.description });
      setFileBase64('');
      setFilename('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      resetForm();
    }
  }, [isOpen, oppositionBase]);

  const isNew = !oppositionBase?._id;
  const hasFile = Boolean(fileBase64) || Boolean(oppositionBase?.fileUrl);

  return (
    <Modal
      open={isOpen}
      destroyOnClose
      afterClose={resetForm}
      onOk={onSaveBase}
      onCancel={() => {
        resetForm();
        onCancel();
      }}
      title={title}
      okButtonProps={{
        disabled: !name || !description || (isNew && !fileBase64),
        loading: createLoading || updateLoading
      }}
    >
      <Input value={name} placeholder='Nombre' onChange={({ target }) => onChangeBase(target.value, 'name')} />
      <div className={clsx('flex flex-col space-y-2 w-full mt-4')}>
        <label className={clsx(
          'text-sm font-medium transition-colors duration-300',
          'text-gray-700 dark:text-gray-300'
        )}>
          Descripción
        </label>
        <TextArea
          value={description}
          onChange={({ target }) => onChangeBase(target.value, 'description')}
          rows={4}
          className={clsx(
            'w-full transition-all duration-300',
            'border-gray-300 dark:border-gray-600',
            'text-gray-900 dark:text-white',
            'rounded-lg'
          )}
        />
      </div>
      <div className={clsx('flex flex-col space-y-2 w-full mt-4')}>
        <label className={clsx(
          'text-sm font-medium transition-colors duration-300',
          'text-gray-700 dark:text-gray-300'
        )}>
          Archivo PDF
        </label>
        <div className={clsx(
          'flex items-center gap-3 p-4 rounded-lg border-2 transition-all duration-300',
          'bg-gray-50 border-gray-200',
          'dark:bg-surface-medium dark:border-borders',
          'hover:border-blue-400 dark:hover:border-blue-500',
          'hover:shadow-sm'
        )}>
          <Button
            onClick={onClickUpload}
            className={clsx(
              'flex items-center gap-2 transition-all duration-300',
              'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
              'border-none text-white shadow-sm hover:shadow-md'
            )}
            icon={<IconUpload size={16} />}
          >
            {isNew ? 'Subir PDF' : 'Cambiar PDF'}
          </Button>
          {hasFile && (
            <div className={clsx(
              'flex items-center gap-2 flex-1 min-w-0',
              'text-sm text-gray-600 dark:text-gray-400'
            )}>
              <IconFile size={16} className="flex-shrink-0" />
              <span className="truncate">{filename || oppositionBase?.fileUrl}</span>
            </div>
          )}
          <input
            ref={fileInputRef}
            className="hidden"
            type="file"
            accept=".pdf,application/pdf"
            onChange={onFileSelect}
          />
        </div>
      </div>
    </Modal>
  );
};

type PropsRemove = PropsWithChildren<{
  isOpen: boolean
  oppositionBase?: TOppositionBase
  onCancel: () => void
}>;

const RemoveOppositionBases: FC<PropsRemove> = ({ isOpen, onCancel, oppositionBase }) => {
  const { removeOppositionBase, loading } = useRemoveOppositionBase();

  const onRemoveBase = async () => {
    await removeOppositionBase(oppositionBase?._id ?? '');
    onCancel();
  };

  return (
    <Modal
      open={isOpen}
      onOk={onRemoveBase}
      onCancel={onCancel}
      title="Eliminar bases"
      okButtonProps={{ loading }}
    >
      {`Vas a eliminar las bases "${oppositionBase?.name}". ¿Estás seguro de que quieres continuar?`}
    </Modal>
  );
};

export { ManageOppositionBases, RemoveOppositionBases };
